import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useEducation } from "../../hooks/useEducation";
import "./CoursesList.css";
import "./index.css";
import LeftArrow from "../../assets/new-education/TR_Fade_Left_panel.svg";
import RightArrow from "../../assets/new-education/TR_Fade_Right_panel.svg";
import CourseCard from "./CourseCard";
import CourseReadMore from "./CourseReadMore";
import CourseListLeftArrow from "../../assets/new-education/left-course-list-arrow.svg";
import CourseListRightArrow from "../../assets/new-education/right-course-list-arrow.svg";
import badge1 from "../../assets/new-education/badge_1.svg";
import { useLocation } from "react-router-dom";

const CoursesList = ({ categoryId, featured = false }) => {
  const navigate = useNavigate();
  const {
    courses,
    coursesLoading,
    coursesError,
    fetchCourses,
    fetchFeaturedCourses,
    fetchCategories,
    categories,
    fetchKeywords,
    enrollInCourse,
    fetchMyCourses,
    toggleFavoriteCourse,
  } = useEducation();

  const [filters, setFilters] = useState({
    category: categoryId || "",
    class_type: "",
    search: "",
    keywords: "",
  });

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1); // remove the '#'
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location]);

  const [searchTerm, setSearchTerm] = useState("");
  const [availableKeywords, setAvailableKeywords] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [selectedKeywordId, setSelectedKeywordId] = useState("");
  const [myCourses, setMyCourses] = useState([]);

  // State for carousels
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentCategorySlide, setCurrentCategorySlide] = useState(0);
  const [currentKeywordSlide, setCurrentKeywordSlide] = useState(0);

  const itemsPerSlide = 4;
  const categoriesPerSlide = 6;
  const keywordsPerSlide = 8;

  useEffect(() => {
    fetchCategories();
    loadKeywords();
    loadMyCourses();
  }, []);

  const loadMyCourses = async () => {
    try {
      const myCoursesData = await fetchMyCourses();
      setMyCourses(myCoursesData || []);
    } catch (error) {
      console.error("Failed to load my courses:", error);
    }
  };

  const loadKeywords = async () => {
    setKeywordsLoading(true);
    try {
      if (typeof fetchKeywords === "function") {
        const keywords = await fetchKeywords();
        setAvailableKeywords(keywords || []);
      } else {
        console.warn(
          "fetchKeywords function not available in useEducation hook"
        );
        setAvailableKeywords([]);
      }
    } catch (error) {
      console.error("Failed to load keywords:", error);
    } finally {
      setKeywordsLoading(false);
    }
  };

  // Auto-enroll helper function
  const autoEnrollInCourse = async (courseId) => {
    try {
      // Check if already enrolled
      const isAlreadyEnrolled = myCourses.some(
        (course) => course.course === courseId
      );

      if (!isAlreadyEnrolled) {
        await enrollInCourse(courseId);
        // Refresh my courses list
        await fetchMyCourses();
        await loadMyCourses();
      }
    } catch (error) {
      console.error("Failed to auto-enroll in course:", error);
    }
  };

  // Handle course card click
  const handleCourseClick = async (courseId) => {
    await autoEnrollInCourse(courseId);
    navigate(`/education/courses/${courseId}`);
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (e, courseId) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // First ensure user is enrolled
      await autoEnrollInCourse(courseId);

      // Find the enrolled course
      const enrolledCourse = myCourses.find(
        (course) => course.course === courseId
      );

      if (enrolledCourse) {
        await toggleFavoriteCourse(enrolledCourse.id);
        // Refresh my courses list to get updated favorite status
        await loadMyCourses();
      }
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timer;
      return (term) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const queryFilters = { ...filters };
          queryFilters.search = term;

          if (!queryFilters.category) delete queryFilters.category;
          if (!queryFilters.class_type) delete queryFilters.class_type;
          if (!queryFilters.keywords) delete queryFilters.keywords;

          fetchCourses(queryFilters);
        }, 500);
      };
    })(),
    [filters, fetchCourses]
  );

  // Handle search term changes separately
  useEffect(() => {
    if (searchTerm !== filters.search) {
      debouncedSearch(searchTerm);
    }
  }, [searchTerm, filters.search, debouncedSearch]);

  useEffect(() => {
    if (featured) {
      fetchFeaturedCourses();
    } else {
      if (searchTerm === filters.search) {
        const queryFilters = { ...filters };
        if (!queryFilters.category) delete queryFilters.category;
        if (!queryFilters.class_type) delete queryFilters.class_type;
        if (!queryFilters.keywords) delete queryFilters.keywords;

        fetchCourses(queryFilters);
      }
    }
  }, [
    featured,
    fetchCourses,
    fetchFeaturedCourses,
    filters.category,
    filters.class_type,
    filters.keywords,
    searchTerm,
    filters.search,
    filters,
  ]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === "search") {
      setSearchTerm(value);
      return;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleCategorySelect = (categoryId) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      category: categoryId,
    }));
  };

  const handleKeywordSelect = (keyword) => {
    const newKeywordValue = selectedKeywordId === keyword.id ? "" : keyword.id;
    setSelectedKeywordId(newKeywordValue);

    setFilters((prevFilters) => ({
      ...prevFilters,
      keywords: newKeywordValue,
    }));
  };

  // Functions for course carousel
  const totalSlides = Math.ceil(courses.length / itemsPerSlide);

  const nextSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide >= totalSlides - 1 ? 0 : prevSlide + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide <= 0 ? totalSlides - 1 : prevSlide - 1
    );
  };

  // Functions for category carousel
  const nextCategorySlide = () => {
    const totalCategorySlides = Math.ceil(
      categories.length / categoriesPerSlide
    );
    setCurrentCategorySlide(
      (prevSlide) => (prevSlide + 1) % totalCategorySlides
    );
  };

  const prevCategorySlide = () => {
    const totalCategorySlides = Math.ceil(
      categories.length / categoriesPerSlide
    );
    setCurrentCategorySlide(
      (prevSlide) => (prevSlide - 1 + totalCategorySlides) % totalCategorySlides
    );
  };

  // Functions for keyword carousel
  const nextKeywordSlide = () => {
    const totalKeywordSlides = Math.ceil(
      availableKeywords.length / keywordsPerSlide
    );
    setCurrentKeywordSlide((prevSlide) => (prevSlide + 1) % totalKeywordSlides);
  };

  const prevKeywordSlide = () => {
    const totalKeywordSlides = Math.ceil(
      availableKeywords.length / keywordsPerSlide
    );
    setCurrentKeywordSlide(
      (prevSlide) => (prevSlide - 1 + totalKeywordSlides) % totalKeywordSlides
    );
  };

  if (coursesLoading && !searchTerm) return <div>Loading courses...</div>;
  if (coursesError) return <div>Error loading courses: {coursesError}</div>;

  // Calculate slice indices for categories and keywords carousels
  const catStartIndex = currentCategorySlide * categoriesPerSlide;
  const catEndIndex = catStartIndex + categoriesPerSlide;
  const visibleCategories = categories.slice(catStartIndex, catEndIndex);

  const keyStartIndex = currentKeywordSlide * keywordsPerSlide;
  const keyEndIndex = keyStartIndex + keywordsPerSlide;
  const visibleKeywords = availableKeywords.slice(keyStartIndex, keyEndIndex);

  const totalCategorySlides = Math.ceil(categories.length / categoriesPerSlide);
  const totalKeywordSlides = Math.ceil(
    availableKeywords.length / keywordsPerSlide
  );

  const getCategoryIcon = (category) => {
    return category.icon_url || badge1;
  };

  return (
    <div className="courses-list">
      {!featured && (
        <>
          {/* Category Filter Section */}
          <div
            style={{
              display: "flex",
              width: "100%",
              gap: "9.75px",
              justifyContent: "center",
            }}
          >
            <div className="course-filter-section course-filter-category">
              <div
                className="education_course_title_info"
                style={{
                  position: "absolute",
                  top: "-39px",
                  left: "35%",
                  fontSize: "14px",
                }}
              >
                Dine gennemførte fag
              </div>

              <div className="course-carousel-container course-category-carousel">
                <div className="course-carousel-wrapper">
                  <div className="courses-carousel category-items-container">
                    <div className="category-items">
                      {categories.map((category) => (
                        <div
                          key={category.id}
                          className="category-item badge-item"
                          title={`${category.name} - ${
                            category.is_completed ? "Completed" : "In Progress"
                          }`}
                        >
                          <img
                            src={getCategoryIcon(category)}
                            alt={`${category.name} badge`}
                            width={30}
                            height={30}
                            style={{
                              filter: category.is_completed
                                ? "brightness(0) saturate(100%) invert(60%) sepia(100%) saturate(1000%) hue-rotate(25deg) brightness(1.1) contrast(1)"
                                : undefined,
                              opacity: category.is_completed ? 0.95 : 1,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <CourseReadMore />

      {courses.length === 0 ? (
        <div className="no-courses">No courses found.</div>
      ) : (
        <div
          className="course-carousel-container course__bubble"
          id="course-lists"
        >
          <div style={{ display: "flex", top: "-20px", position: "absolute" }}>
            <div className="education_course_title_info">Fag</div>
            {/* Keywords Filter Section */}
            <div
              className="course-filter-section"
              style={{ display: "flex", gap: "20px" }}
            >
              {/* Main Filters */}
              <div className="search-group">
                <input
                  type="text"
                  id="search"
                  name="search"
                  value={searchTerm}
                  onChange={handleFilterChange}
                  placeholder="Search courses..."
                />
                {coursesLoading && searchTerm && (
                  <span className="search-loading">Searching...</span>
                )}
              </div>

              {keywordsLoading ? (
                <div>Loading keywords...</div>
              ) : (
                <div className="course-carousel-container course-keyword-carousel">
                  <div className="course-carousel-wrapper">
                    <button
                      onClick={prevKeywordSlide}
                      className={`course-carousel-arrow course-carousel-arrow-left ${
                        currentKeywordSlide === 0 ? "disabled" : ""
                      }`}
                      disabled={currentKeywordSlide === 0}
                    >
                      <img
                        width={29}
                        height={30}
                        alt="Arrow left"
                        src={LeftArrow}
                      />
                    </button>

                    <div className="courses-carousel keyword-items-container">
                      <div className="keyword-items">
                        {visibleKeywords.map((keyword) => (
                          <div
                            key={keyword.id}
                            className={`keyword-item ${
                              selectedKeywordId === keyword.id ? "active" : ""
                            }`}
                            onClick={() => handleKeywordSelect(keyword)}
                          >
                            {keyword.name}
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={nextKeywordSlide}
                      className={`course-carousel-arrow course-carousel-arrow-right ${
                        currentKeywordSlide === totalKeywordSlides - 1
                          ? "disabled"
                          : ""
                      }`}
                      disabled={
                        currentKeywordSlide === totalKeywordSlides - 1 ||
                        totalKeywordSlides <= 1
                      }
                    >
                      <img
                        width={29}
                        height={30}
                        alt="Arrow right"
                        src={RightArrow}
                      />
                    </button>
                  </div>

                  {totalKeywordSlides > 1 && (
                    <div className="carousel-indicators">
                      {Array.from({ length: totalKeywordSlides }).map(
                        (_, index) => (
                          <button
                            key={index}
                            className={`carousel-indicator ${
                              currentKeywordSlide === index ? "active" : ""
                            }`}
                            onClick={() => setCurrentKeywordSlide(index)}
                          />
                        )
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div
            className="course-carousel-wrapper"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <button
              onClick={prevSlide}
              className={`course-carousel-arrow course-carousel-arrow-left ${
                currentSlide === 0 ? "disabled" : ""
              }`}
              disabled={currentSlide === 0}
              style={{
                position: "absolute",
                left: "10px",
                zIndex: 10,
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <img
                src={CourseListLeftArrow}
                alt="Course left arrow"
                width={59}
                height={268}
              />
            </button>

            <div
              className="courses-carousel"
              style={{
                overflow: "hidden",
                width: "calc(100% - 140px)",
                margin: "0 70px",
                position: "relative",
              }}
            >
              <div
                className="carousel-track"
                style={{
                  display: "flex",
                  transform: `translateX(-${currentSlide * 100}%)`,
                  transition: "transform 0.3s ease-in-out",
                }}
              >
                {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                  <div
                    key={slideIndex}
                    className="carousel-slide"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      flexShrink: 0,
                      gap: "8px",
                      justifyContent: "space-between",
                      alignItems: "stretch",
                    }}
                  >
                    {courses
                      .slice(
                        slideIndex * itemsPerSlide,
                        (slideIndex + 1) * itemsPerSlide
                      )
                      .map((course) => (
                        <div
                          key={course.id}
                          className="course-card-wrapper"
                          style={{
                            flex: "1 1 0 !important",
                            minWidth: "0 !important",
                            width: "23% !important",
                            maxWidth: "23% !important",
                          }}
                        >
                          <CourseCard
                            course={course}
                            myCourses={myCourses}
                            onCourseClick={handleCourseClick}
                            onFavoriteToggle={handleFavoriteToggle}
                          />
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={nextSlide}
              className={`course-carousel-arrow course-carousel-arrow-right ${
                currentSlide === totalSlides - 1 ? "disabled" : ""
              }`}
              disabled={currentSlide === totalSlides - 1 || totalSlides <= 1}
              style={{
                position: "absolute",
                right: "10px",
                zIndex: 10,
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <img
                src={CourseListRightArrow}
                alt="Course right arrow"
                width={59}
                height={268}
              />
            </button>
          </div>

          {totalSlides > 1 && (
            <div className="carousel-indicators">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`carousel-indicator ${
                    currentSlide === index ? "active" : ""
                  }`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoursesList;
